import * as selectors from '../selectors';
import * as actions from '../actions';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { retry, mergeMap, map, catchError, of, distinctUntilChanged, filter, withLatestFrom, switchMap } from 'rxjs';
import { EnergyAnalyticsService } from '@twaice-fe/frontend/shared/services';
import { CustomerConfigurationService } from '../../../services/src/lib/customer-configuration.service';
import { getCustomerBk } from '../selectors/configs.selectors';

const { configsAction } = actions;
const { systemSelectors, configsSelectors } = selectors;

@Injectable()
export class ConfigEffects {
  fetchStorageNameMappings$ = createEffect(() =>
    this.store.pipe(
      select(systemSelectors.getSelected),
      distinctUntilChanged(
        (previous, current) => previous?.customerBk === current?.customerBk && previous?.systemBk === current?.systemBk
      ),
      filter((system) => !!system && !!system.customerBk && !!system.systemBk),
      withLatestFrom(this.store.select(configsSelectors.getStorageNameMappings)),
      filter(([system, storageMappings]) => storageMappings[`${system?.customerBk}/${system?.systemBk}`] === undefined),
      mergeMap(
        ([system]) =>
          /* eslint-disable @typescript-eslint/no-non-null-assertion */
          this.energyAnalyticsService
            .fetchStorageNameMappings({ customerBk: system!.customerBk!, systemBk: system!.systemBk! })
            .pipe(
              retry(3),
              map((mappings) =>
                configsAction.loadNameMappingSuccess({
                  customerBk: system!.customerBk!,
                  systemBk: system!.systemBk!,
                  componentNameMapping: mappings.componentNameMapping,
                  levelNameMapping: mappings.levelNameMapping,
                })
              ),
              catchError((error) => {
                console.error('[Error/Fetch storage name mappings]', error);
                return of(configsAction.loadNameMappingFailure({ error }));
              })
            )
        /* eslint-enable @typescript-eslint/no-non-null-assertion */
      )
    )
  );

  onCustomerChange$ = createEffect(() =>
    this.store.pipe(
      select(getCustomerBk),
      filter((customerBk): customerBk is string => !!customerBk),
      distinctUntilChanged(),
      map((customerBk) => configsAction.fetchCustomerConfiguration({ customerBk }))
    )
  );

  fetchCustomerConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(configsAction.fetchCustomerConfiguration),
      switchMap(({ customerBk }) =>
        this.customerConfigurationService.fetchCustomerConfiguration(customerBk).pipe(
          map(({ presignedLogoUrl }) => configsAction.fetchCustomerConfigurationSuccess({ logoUrl: presignedLogoUrl })),
          catchError((error) => {
            console.error('[Error/Fetch customer configuration]', error);
            return of(configsAction.fetchCustomerConfigurationError({ error }));
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private energyAnalyticsService: EnergyAnalyticsService,
    private customerConfigurationService: CustomerConfigurationService,
    protected store: Store
  ) {}
}
